import { isAfter } from 'date-fns';
import { isNull } from 'lodash';
import { useMemo } from 'react';

import { Device, useDevice } from '@/api/devices';
import { useNow } from '@/hooks/useNow';

export type SpotterModeChangeStatus = {
    isSatelliteModeChanging: boolean;
    isCellularModeChanging: boolean;
    hdrEnabled: boolean;
    pendingModeSettingGroup: 'satellite' | 'cellular' | null;
    modeChangeStaleAt: string | null;
    staleChangeRequest: boolean;
};

export const useSpotterModeChangeStatus = ({ spotterId }: { spotterId?: string | null }): SpotterModeChangeStatus => {
    const now = useNow(60);
    const { data: device } = useDevice({ spotterId });

    return useMemo(() => {
        const isSatelliteModeChanging = checkSatelliteDataSettingsChanging(device);
        const isCellularModeChanging = checkCellularDataSettingsChanging(device);

        // We're currently only allowing one set of mode settings to change at a time. If we ever allow combining sets, this will need to be updated.
        const pendingModeSettingGroup = isSatelliteModeChanging
            ? 'satellite'
            : isCellularModeChanging
              ? 'cellular'
              : null;
        const modeChangeStaleAt = device?.modeChangeStaleAt ?? null;
        return {
            isSatelliteModeChanging,
            isCellularModeChanging,
            hdrEnabled: isHDREnabled(device),
            pendingModeSettingGroup,
            modeChangeStaleAt,
            staleChangeRequest: Boolean(modeChangeStaleAt && isAfter(now, modeChangeStaleAt)),
        };
    }, [device, now]);
};

function checkSatelliteDataSettingsChanging(device?: Device) {
    if (!device) {
        return false;
    }
    const { currentMode, targetMode, currentMessagePeriod, targetMessagePeriod } = device;
    if (isNull(targetMode) || isNull(targetMessagePeriod)) {
        return false;
    }
    return currentMode !== targetMode || currentMessagePeriod !== targetMessagePeriod;
}

// target cellular settings can have null values!
// target cellular settings will only contain the settings that need to be changed. the absence of a setting means the setting will not be changed
function checkCellularDataSettingsChanging(device?: Device) {
    if (!device) {
        return false;
    }
    if (isNull(device.cellularData.targetCellularFirmwareSettings)) {
        return false;
    }

    const currentCellularSettings = device.cellularData.currentCellularFirmwareSettings;
    const targetCellularSettings = device.cellularData.targetCellularFirmwareSettings;

    return Object.entries(targetCellularSettings).some(([key, value]) => {
        // target cellular settings only contain settings that need to be changed, so if the value is null, it means it's not changing
        if (isNull(value)) {
            return false;
        }
        // @ts-expect-error typescript pls, you know they're the same type
        return currentCellularSettings[key] !== value;
    });
}

const isHDREnabled = (device?: Device) => {
    if (!device) {
        return false;
    }
    const { hdrRecordsPerMessage } = device.cellularData.currentCellularFirmwareSettings;
    return !!hdrRecordsPerMessage;
};
